import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { CSSProperties } from "react"
import { GridList, GridListItem } from "react-aria-components"
import { useTranslation } from "react-i18next"

import { default as Vehicle } from "@/assets/illustrations/vehicle.svg"
import { LinkButton } from "@/globalComponents/atoms/Button"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import { cn } from "@/utilities/etc"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import {
  useScopedNavigate,
  useScopedTo,
} from "@/utilities/hooks/useScopedRouting"

import styles from "../index.module.css"

export const Route = createFileRoute("/_authenticated/account/vehicles/")({
  component: VehiclesIndexRouteComponent,
})

export function VehiclesIndexRouteComponent() {
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const scopedNavigate = useScopedNavigate()
  const scoped = useScopedTo()
  const vehicles = me?.inflowUser.savedPlates
  const { t } = useTranslation(["common"])

  const addVehicleButton = (label: string, style?: CSSProperties) => (
    <LinkButton
      buttonType="primary"
      {...scoped({ to: "/account/vehicles/create" })}
      search={{ plate: undefined, redirect: undefined }}
      style={style}
    >
      <Icon name="add" slot="left" />
      {label}
    </LinkButton>
  )

  return (
    <>
      {!vehicles?.length ? (
        <div className="centered-content">
          <Vehicle />
          {addVehicleButton(t("account.vehicles.addFirstVehicle"), {
            marginTop: "var(--space-5)",
          })}
        </div>
      ) : (
        <>
          {addVehicleButton(t("account.vehicles.addVehicle"))}
          <GridList
            items={vehicles}
            className={styles.linkList}
            selectionBehavior="replace"
            aria-label={t("account.vehicles.label")}
          >
            {(item) => (
              <GridListItem
                id={`${item.lastUsed}${item.nickname}${item.plate}`}
                key={`${item.lastUsed}${item.nickname}${item.plate}`}
                textValue={
                  item.nickname
                    ? `${item.nickname} - ${item.plate}`
                    : item.plate
                }
                onAction={() =>
                  scopedNavigate({
                    to: "/account/vehicles/edit",
                    search: { plate: item.plate },
                  })
                }
              >
                <Link
                  {...scoped({ to: "/account/vehicles/edit" })}
                  search={{ plate: item.plate }}
                  className={cn(styles.links, "bold")}
                >
                  <VehicleRow item={item} />
                </Link>
              </GridListItem>
            )}
          </GridList>
        </>
      )}
    </>
  )
}

function VehicleRow({ item }: { item: { nickname?: string; plate: string } }) {
  return (
    <>
      <div className={styles.linkIcon}>
        <Icon name="car" />
      </div>
      <div>
        {item.nickname && <Text className="bold">{item.nickname}</Text>}
        {item.nickname ? (
          <Text level={2}>{item.plate}</Text>
        ) : (
          <Text className="bold">{item.plate}</Text>
        )}
      </div>
      <Icon name="chevronRight" />
    </>
  )
}
